import { useTheme } from '@mui/material/styles';
import { formatAsKpi } from 'domain/kpis/utils';
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { NameType } from 'recharts/types/component/DefaultTooltipContent';
import Card from 'ui/views/cards/Card';
import { asMonthYear, asUnixMillis } from 'util/dateUtils';
import { fontSizeSmall, shape } from 'ui/theme/themeBluePlanet';
import { halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import styled from '@emotion/styled';

export interface BarSeriesPoint {
  x: number;
  y: number;
  comparisonY?: number;
}

export interface ChartData {
  data: BarSeriesPoint[];
}

const ChartContainer = styled.div`
  position: relative;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${halfSpacing};
`;

const TooltipColor = styled.div<{ color: string }>`
  width: ${quarterSpacing};
  height: ${quarterSpacing};
  border-radius: ${shape.borderRadius / 2}px;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

interface Props {
  name: string;
  main: ChartData;
  comparison?: ChartData;
  unit?: string;
  showXAxis?: boolean;
  showYAxis?: boolean;
}

export default function BarChart(props: Props) {
  const theme = useTheme();
  const textColor = theme.bluePlanetPalette.grey.dark;
  const fontWeightMedium = theme.typography.fontWeightMedium;

  // Calculate dynamic Y-axis width based on max value
  const maxMainValue = Math.max(...props.main.data.map(d => d.y), 0);
  const maxComparisonValue = props.comparison ? Math.max(...props.comparison.data.map(d => d.y), 0) : 0;
  const maxValue = Math.max(maxMainValue, maxComparisonValue);
  const formattedMaxValue = formatAsKpi(maxValue);
  const yAxisWidth = Math.max(25, Math.min(80, formattedMaxValue.length * 8 + 8));

  // Merge main and comparison data if comparison exists
  let chartData = props.main.data;

  if (props.comparison && props.comparison.data.length > 0) {
    // Create a map of comparison values by timestamp
    const comparisonMap = new Map<number, number>();
    props.comparison.data.forEach(point => {
      comparisonMap.set(point.x, point.y);
    });

    // Merge comparison values into main data
    chartData = props.main.data.map(point => ({
      x: point.x,
      y: point.y,
      comparisonY: comparisonMap.get(point.x),
    }));
  }

  // Handle empty data
  const displayData = chartData.length > 0 ? chartData : [{ x: asUnixMillis(new Date()), y: 0 }];

  return (
    <ChartContainer>
      <ResponsiveContainer height={200}>
        <ComposedChart
          data={displayData}
          margin={{
            top: 10,
            right: props.showYAxis ? 10 : 0,
            left: 0,
            bottom: props.showXAxis ? 0 : 0,
          }}
        >
          <CartesianGrid vertical={false} />
          {props.showXAxis && (
            <XAxis
              dataKey="x"
              tickFormatter={value => asMonthYear(value)}
              style={{ fill: textColor, fontWeight: fontWeightMedium as number, fontSize: fontSizeSmall }}
              tickLine={false}
              minTickGap={20}
            />
          )}
          {props.showYAxis && (
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={value => formatAsKpi(Number(value))}
              style={{ fill: textColor, fontWeight: fontWeightMedium as number, fontSize: fontSizeSmall }}
              allowDecimals={false}
              width={yAxisWidth}
            />
          )}
          <Tooltip content={<ToolTip unit={props.unit} hasComparison={!!props.comparison} />} />

          {/* Comparison bar (rendered first so it appears behind) */}
          {props.comparison && (
            <Bar
              dataKey="comparisonY"
              name="Previous"
              fill={theme.bluePlanetPalette.grey.medium}
              maxBarSize={25}
              radius={[4, 4, 0, 0]}
            />
          )}

          {/* Main bar */}
          <Bar
            dataKey="y"
            name={props.name}
            fill={theme.bluePlanetPalette.blue.main}
            maxBarSize={25}
            radius={[4, 4, 0, 0]}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

function ToolTip(props: TooltipProps<number, NameType> & { unit?: string; hasComparison?: boolean }) {
  const theme = useTheme();
  const { active, payload, unit, hasComparison } = props;

  if (active && payload && payload.length) {
    const currentValue = payload.find(p => p.dataKey === 'y')?.value;
    const comparisonValue = payload.find(p => p.dataKey === 'comparisonY')?.value;
    const currentDate = payload[0]?.payload?.x;

    // Calculate date one year ago for comparison label
    const currentDateObj = new Date(currentDate);
    const previousYearDate = new Date(currentDateObj);
    previousYearDate.setFullYear(currentDateObj.getFullYear() - 1);

    return (
      <Card elevation={2}>
        {currentValue !== undefined ? (
          <>
            <TooltipRow>
              <TooltipColor color={theme.bluePlanetPalette.blue.main} />
              <span>
                {asMonthYear(currentDate)}: {formatAsKpi(currentValue)} {unit}
              </span>
            </TooltipRow>
            {hasComparison && comparisonValue !== undefined && (
              <TooltipRow className="text-metadata">
                <TooltipColor color={theme.bluePlanetPalette.grey.medium} />
                <span>
                  {asMonthYear(previousYearDate)}: {formatAsKpi(comparisonValue)} {unit}
                </span>
              </TooltipRow>
            )}
          </>
        ) : (
          <p>No data</p>
        )}
      </Card>
    );
  }

  return null;
}
